import React, { useState, useRef, useCallback } from 'react'
import { Dialog, Box, Stack, Button, Flex, Text, Label, TextArea } from '@sanity/ui'
import { FieldPicker } from './FieldPicker'
import { LivePreview } from './LivePreview'

interface TemplateEditorModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  value: string
  onChange: (value: string) => void
  maxLength?: number
}

/**
 * TemplateEditorModal - Full-featured template editor with field picker and live preview
 */
export function TemplateEditorModal({
  isOpen,
  onClose,
  title,
  value,
  onChange,
  maxLength,
}: TemplateEditorModalProps) {
  const [localValue, setLocalValue] = useState(value)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Track cursor position for field insertion
  const [cursorPosition, setCursorPosition] = useState<number>(0)

  // Handle template text change
  const handleTemplateChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value)
    setCursorPosition(e.target.selectionStart || 0)
  }, [])

  // Handle cursor position updates
  const handleSelect = useCallback((e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement
    setCursorPosition(target.selectionStart || 0)
  }, [])

  // Insert field at cursor position
  const handleInsertField = useCallback((fieldPath: string) => {
    const textarea = textareaRef.current
    if (!textarea) {
      // Fallback: append to end
      setLocalValue((prev) => prev + fieldPath)
      return
    }

    const start = cursorPosition
    const before = localValue.substring(0, start)
    const after = localValue.substring(start)
    const newValue = before + fieldPath + after
    setLocalValue(newValue)

    // Update cursor position to after inserted text
    const newCursorPos = start + fieldPath.length
    setCursorPosition(newCursorPos)

    // Focus and set cursor position after state update
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }, [localValue, cursorPosition])

  // Save and close
  const handleSave = useCallback(() => {
    onChange(localValue)
    onClose()
  }, [localValue, onChange, onClose])

  // Cancel and close
  const handleCancel = useCallback(() => {
    setLocalValue(value) // Reset to original
    onClose()
  }, [value, onClose])

  // Reset local value when modal opens with new value
  React.useEffect(() => {
    if (isOpen) {
      setLocalValue(value)
      setCursorPosition(value.length)
    }
  }, [isOpen, value])

  if (!isOpen) {
    return null
  }

  return (
    <Dialog
      id="template-editor-modal"
      header={title}
      onClose={handleCancel}
      width={3}
      position="fixed"
      zOffset={600000}
    >
      <Box padding={4}>
        <Stack gap={4}>
          {/* Main content - 3 panel layout */}
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 300px',
              gridTemplateRows: '1fr auto',
              gap: '16px',
              minHeight: '500px',
            }}
          >
            {/* Left: Template Editor */}
            <Box style={{ gridColumn: '1', gridRow: '1' }}>
              <Stack gap={2} style={{ height: '100%' }}>
                <Flex justify="space-between" align="center">
                  <Label size={1}>Template</Label>
                  {maxLength && (
                    <Text size={0} muted>
                      {localValue.length} / {maxLength} chars
                    </Text>
                  )}
                </Flex>
                <TextArea
                  ref={textareaRef}
                  value={localValue}
                  onChange={handleTemplateChange}
                  onSelect={handleSelect}
                  onClick={handleSelect}
                  onKeyUp={handleSelect}
                  style={{
                    flex: 1,
                    minHeight: '300px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: 1.5,
                    resize: 'vertical',
                  }}
                  placeholder="Enter your prompt template here. Use {{field.path}} for context variables."
                />
                <Text size={0} muted>
                  Use {'{{variable}}'} for values, {'{{#if condition}}...{{/if}}'} for conditionals, {'{{#each array}}...{{/each}}'} for loops
                </Text>
              </Stack>
            </Box>

            {/* Right: Field Picker */}
            <Box
              style={{
                gridColumn: '2',
                gridRow: '1',
                borderLeft: '1px solid #eee',
                paddingLeft: '16px',
              }}
            >
              <Stack gap={2} style={{ height: '100%' }}>
                <Label size={1}>Available Fields</Label>
                <FieldPicker onInsertField={handleInsertField} />
              </Stack>
            </Box>

            {/* Bottom: Live Preview */}
            <Box style={{ gridColumn: '1 / -1', gridRow: '2', borderTop: '1px solid #eee', paddingTop: '16px' }}>
              <Stack gap={2}>
                <Label size={1}>Live Preview</Label>
                <Box style={{ maxHeight: '250px' }}>
                  <LivePreview template={localValue} />
                </Box>
              </Stack>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Flex gap={2} justify="flex-end">
            <Button
              text="Cancel"
              mode="ghost"
              onClick={handleCancel}
            />
            <Button
              text="Save Changes"
              tone="primary"
              onClick={handleSave}
            />
          </Flex>
        </Stack>
      </Box>
    </Dialog>
  )
}

export default TemplateEditorModal
